'use strict';

const libPictView = require('pict-view');

/**
 * ProjectActivity — the #/ProjectActivity[/:IDProject] screen. A per-project data-activity view: create/update
 * pressure for Documents, Observations and Line Items over time, plus a recent-activity stream of the latest
 * create/update events on the project.
 *
 * Thin shell over the shared ActivityObservability provider (renders into #AO-Project-Body).
 */
class ProjectActivity extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		// When embedded (the ProjectWorkspace Data ▸ Data Volume tab) this holds the host selector; render() then
		// targets it, so the .ao-wrap shell — and thus the shared provider's global #AO-Project-Body / chart assigns
		// that live inside it — land in the tab host instead of the app container.
		this._embedDestination = null;
	}

	get provider() { return this.pict.providers.ActivityObservability; }

	/** Render into the embed host when embedded. An explicit destination argument always wins. */
	render(pRenderableHash, pRenderDestinationAddress, pTemplateRecordAddress, pRootRenderable)
	{
		const tmpDestination = (typeof pRenderDestinationAddress === 'string') ? pRenderDestinationAddress : (this._embedDestination || undefined);
		return super.render(pRenderableHash, tmpDestination, pTemplateRecordAddress, pRootRenderable);
	}

	/** Embed the data-volume dashboard into a host for a fixed project (skips the standalone picker). Unlike
	 *  SamplePlan/ProjectFunding, the SHARED ActivityObservability provider paints into global ids and never
	 *  redraws its chart on a plain repaint — so: render the shell into the host FIRST (creates #AO-Project-Body),
	 *  then either load (project changed) or just redraw the chart from cache (same-project repaint). The guard on
	 *  projectActivity.IDProject keeps a routine workspace repaint from re-firing the expensive COUNT fan-out. */
	openInto(pIDProject, pHostSelector)
	{
		this._embedDestination = pHostSelector;
		const tmpProvider = this.provider;
		const tmpSameProject = !!(tmpProvider && tmpProvider.projectActivity && (Number(tmpProvider.projectActivity.IDProject) === Number(pIDProject)));
		if (tmpProvider && tmpProvider.projectActivity) { tmpProvider.projectActivity.IDProject = Number(pIDProject); }
		this.render();   // paint .ao-wrap (+ #AO-Project-Body) into the host; onAfterRender fills the body
		if (tmpProvider && !tmpSameProject) { tmpProvider.loadProjectActivityAsync(pIDProject); }
		else if (tmpProvider && (typeof tmpProvider._drawProjectChart === 'function')) { tmpProvider._drawProjectChart(); }
	}

	/** Leave embedded mode — the standalone #/ProjectActivity screen renders to the app container again. */
	exitEmbed() { this._embedDestination = null; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider._ensureStyles();
			tmpProvider.renderProjectActivity();
			// No auto-load here: there is no default project, and the :IDProject sub-route loads explicitly — so
			// loading from onAfterRender too would double-fire (two identical project-resolve + count passes).
		}
		return super.onAfterRender(pRenderable);
	}
}

module.exports = ProjectActivity;
module.exports.default_configuration =
{
	ViewIdentifier: 'ProjectActivity',
	// Reusable app-shell Dashboard Mode — render full browser width (see PlatformConfiguration-Router _applyDashboardMode).
	DashboardMode: true,
	DefaultRenderable: 'ProjectActivityWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: '',
	Templates:
	[
		{
			Hash: 'ProjectActivity-Wrap',
			Template: /*html*/`
				<div class="ao-wrap">
					<div class="ao-head">
						<h1 class="ao-title">Project Activity</h1>
						<p class="ao-subtitle">A project&#8217;s data over time — Documents, Observations and Line Items created or updated per day, week or month, plus a stream of the most recent activity.</p>
					</div>
					<div id="AO-Project-Body"></div>
				</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ProjectActivityWrap',
			TemplateHash: 'ProjectActivity-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
